/**
 * Reorder affordance shared by section + question cards (VER-109).
 *
 * A drag handle (native HTML5 DnD) plus explicit move-up / move-down buttons.
 * The buttons are the accessible + testable path; the handle is the mouse path.
 */
import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { DragIndicator, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

export type ReorderControlsProps = {
    label: string;
    onUp: () => void;
    onDown: () => void;
    disableUp: boolean;
    disableDown: boolean;
    /** Props from useDragReorder().handleProps(index) applied to the drag handle. */
    dragHandleProps: {
        draggable: true;
        onDragStart: (e: React.DragEvent) => void;
        onDragEnd: () => void;
    };
};

export const ReorderControls = ({
    label,
    onUp,
    onDown,
    disableUp,
    disableDown,
    dragHandleProps,
}: ReorderControlsProps): JSX.Element => (
    <Box display="flex" alignItems="center">
        <Tooltip title="Drag to reorder">
            <Box
                {...dragHandleProps}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "grab",
                    color: "text.disabled",
                    "&:active": { cursor: "grabbing" },
                }}
            >
                <DragIndicator fontSize="small" />
            </Box>
        </Tooltip>
        <Tooltip title={`Move ${label} up`}>
            <span>
                <IconButton size="small" aria-label={`Move ${label} up`} onClick={onUp} disabled={disableUp}>
                    <KeyboardArrowUp fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
        <Tooltip title={`Move ${label} down`}>
            <span>
                <IconButton size="small" aria-label={`Move ${label} down`} onClick={onDown} disabled={disableDown}>
                    <KeyboardArrowDown fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    </Box>
);
